import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Send, Users, Loader2, Megaphone, Ban, Eye } from "lucide-react";
import {
  useCrmCampanhas,
  useCriarCampanha,
  useDispararCampanha,
  useSegmentoClientes,
  useCrmCanais,
  useCrmCampanhaEnvios,
  useCancelarCampanha,
} from "@/hooks/useCrm";
import { usePlanosAtivos } from "@/hooks/usePlanos";
import {
  CAMPANHA_STATUS_LABEL,
  type CrmCampanha,
  type CrmCampanhaFiltros,
} from "@/types/crm";
import { formatDataHora } from "./crmUtils";

const UFS = [
  "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB",
  "PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO",
];

const STATUS_COLOR: Record<string, string> = {
  rascunho: "bg-[#F3F4F6] text-[#4B5563]",
  agendada: "bg-[#E8E9EA] text-[#252D30]",
  em_andamento: "bg-[#C9C9C9] text-[#1F2937]",
  concluida: "bg-[#252D30] text-white",
  cancelada: "bg-red-50 text-red-700",
};

function podeDisparar(status: string) {
  return status === "rascunho" || status === "agendada";
}

function podeCancelar(status: string) {
  return status === "rascunho" || status === "agendada" || status === "em_andamento";
}

type Props = {
  novaOpen?: boolean;
  onNovaOpenChange?: (open: boolean) => void;
};

export function CrmCampanhas({ novaOpen = false, onNovaOpenChange }: Props) {
  const { data: campanhas = [], isLoading } = useCrmCampanhas();
  const disparar = useDispararCampanha();
  const cancelar = useCancelarCampanha();
  const [detalhe, setDetalhe] = useState<CrmCampanha | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="crm-title text-lg">Campanhas</h3>
        <Button className="rounded-2xl bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => onNovaOpenChange?.(true)}>
          <Plus className="h-4 w-4 mr-2" /> Nova campanha
        </Button>
      </div>

      <div className="crm-card overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-12 text-fynix-muted">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : campanhas.length === 0 ? (
          <p className="text-center text-sm text-fynix-muted py-12">Nenhuma campanha criada ainda.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-fynix-surface-muted text-fynix-muted text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left font-medium px-4 py-3">Nome</th>
                  <th className="text-left font-medium px-4 py-3">Tipo</th>
                  <th className="text-right font-medium px-4 py-3">Destinatários</th>
                  <th className="text-right font-medium px-4 py-3">Enviados</th>
                  <th className="text-left font-medium px-4 py-3">Status</th>
                  <th className="text-right font-medium px-4 py-3">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-fynix-line">
                {campanhas.map((c) => (
                  <tr key={c.id} className="hover:bg-fynix-surface-muted/70">
                    <td className="px-4 py-3">
                      <div className="font-medium text-fynix-ink">{c.nome}</div>
                      {c.descricao && <div className="text-xs text-fynix-muted">{c.descricao}</div>}
                    </td>
                    <td className="px-4 py-3 capitalize text-fynix-ink-soft">{c.tipo}</td>
                    <td className="px-4 py-3 text-right">{c.total_destinatarios}</td>
                    <td className="px-4 py-3 text-right">
                      {c.total_enviados}
                      {c.total_erros > 0 && (
                        <span className="text-red-500 text-xs ml-1">({c.total_erros} erros)</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_COLOR[c.status] ?? STATUS_COLOR.rascunho}`}>
                        {CAMPANHA_STATUS_LABEL[c.status] ?? c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1.5">
                        <Button size="sm" variant="ghost" className="rounded-xl" onClick={() => setDetalhe(c)}>
                          <Eye className="h-3.5 w-3.5 mr-1" /> Detalhe
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-xl"
                          disabled={!podeDisparar(c.status) || disparar.isPending}
                          onClick={() => disparar.mutate(c)}
                        >
                          <Send className="h-3.5 w-3.5 mr-1" /> Disparar
                        </Button>
                        {podeCancelar(c.status) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="rounded-xl text-red-600 hover:text-red-700"
                            disabled={cancelar.isPending}
                            onClick={() => cancelar.mutate(c.id)}
                          >
                            <Ban className="h-3.5 w-3.5 mr-1" /> Cancelar
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <NovaCampanhaDialog open={novaOpen} onOpenChange={(v) => onNovaOpenChange?.(v)} />
      <DetalheCampanhaDialog campanha={detalhe} onClose={() => setDetalhe(null)} />
    </div>
  );
}

function NovaCampanhaDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [nome, setNome] = useState("");
  const [tipo, setTipo] = useState("marketing");
  const [canalId, setCanalId] = useState<string>("");
  const [mensagem, setMensagem] = useState("");
  const [estado, setEstado] = useState<string>("");
  const [cidade, setCidade] = useState("");
  const [planoId, setPlanoId] = useState<string>("");

  const { data: canais = [] } = useCrmCanais();
  const { data: planos = [] } = usePlanosAtivos();
  const criar = useCriarCampanha();

  const filtros: CrmCampanhaFiltros = useMemo(
    () => ({
      estado: estado || null,
      cidade: cidade || null,
      plano_id: planoId || null,
    }),
    [estado, cidade, planoId]
  );

  const { data: segmento, isFetching: contando } = useSegmentoClientes(filtros, open);

  const resetar = () => {
    setNome("");
    setTipo("marketing");
    setCanalId("");
    setMensagem("");
    setEstado("");
    setCidade("");
    setPlanoId("");
  };

  const handleCriar = () => {
    if (!nome.trim() || !mensagem.trim()) return;
    criar.mutate(
      {
        nome: nome.trim(),
        tipo,
        mensagem_texto: mensagem.trim(),
        canal_id: canalId || null,
        filtros,
        total_destinatarios: segmento?.total ?? 0,
      },
      {
        onSuccess: () => {
          resetar();
          onOpenChange(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5" /> Nova campanha
          </DialogTitle>
          <DialogDescription>
            Crie uma lista de distribuição segmentada da sua base de clientes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Nome</Label>
            <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex: Moto elétrica PR" className="rounded-2xl" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={tipo} onValueChange={setTipo}>
                <SelectTrigger className="rounded-2xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="marketing">Marketing</SelectItem>
                  <SelectItem value="renovacao">Renovação</SelectItem>
                  <SelectItem value="onboarding">Onboarding</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Canal (número)</Label>
              <Select value={canalId} onValueChange={setCanalId}>
                <SelectTrigger className="rounded-2xl">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {canais.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Estado (UF)</Label>
              <Select value={estado || "all"} onValueChange={(v) => setEstado(v === "all" ? "" : v)}>
                <SelectTrigger className="rounded-2xl">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {UFS.map((uf) => (
                    <SelectItem key={uf} value={uf}>
                      {uf}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Cidade</Label>
              <Input value={cidade} onChange={(e) => setCidade(e.target.value)} placeholder="Opcional" className="rounded-2xl" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Plano</Label>
            <Select value={planoId || "all"} onValueChange={(v) => setPlanoId(v === "all" ? "" : v)}>
              <SelectTrigger className="rounded-2xl">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os planos</SelectItem>
                {planos.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2 text-sm text-fynix-ink-soft bg-fynix-surface-muted rounded-2xl px-3 py-2">
            <Users className="h-4 w-4 text-fynix-muted" />
            {contando ? (
              <span className="flex items-center gap-1">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Calculando público…
              </span>
            ) : (
              <span>
                <strong>{segmento?.total ?? 0}</strong> cliente(s) com telefone neste segmento.
              </span>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Mensagem</Label>
            <Textarea
              value={mensagem}
              onChange={(e) => setMensagem(e.target.value)}
              rows={4}
              className="rounded-2xl"
              placeholder="Use {nome} para personalizar. Ex: Olá {nome}, temos uma oferta especial…"
            />
            <p className="text-xs text-fynix-muted">
              Variáveis disponíveis: {"{nome}"}. O disparo respeita a lista de opt-out.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-2xl" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          <Button
            className="rounded-2xl bg-fynix-brand hover:bg-fynix-brand-dark"
            onClick={handleCriar}
            disabled={criar.isPending || !nome.trim() || !mensagem.trim()}
          >
            {criar.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Salvar rascunho
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetalheCampanhaDialog({
  campanha,
  onClose,
}: {
  campanha: CrmCampanha | null;
  onClose: () => void;
}) {
  const { data: envios = [], isLoading } = useCrmCampanhaEnvios(campanha?.id);

  return (
    <Dialog open={!!campanha} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{campanha?.nome}</DialogTitle>
          <DialogDescription>
            {campanha ? CAMPANHA_STATUS_LABEL[campanha.status] ?? campanha.status : ""}
            {campanha?.tipo ? ` · ${campanha.tipo}` : ""}
          </DialogDescription>
        </DialogHeader>

        {campanha && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="rounded-2xl bg-fynix-surface-muted p-3">
                <div className="text-xs text-fynix-muted">Destinatários</div>
                <div className="crm-title text-xl">{campanha.total_destinatarios}</div>
              </div>
              <div className="rounded-2xl bg-fynix-surface-muted p-3">
                <div className="text-xs text-fynix-muted">Enviados</div>
                <div className="crm-title text-xl">{campanha.total_enviados}</div>
              </div>
              <div className="rounded-2xl bg-fynix-surface-muted p-3">
                <div className="text-xs text-fynix-muted">Erros</div>
                <div className="crm-title text-xl">{campanha.total_erros}</div>
              </div>
            </div>

            <div>
              <div className="text-xs text-fynix-muted mb-1">Mensagem</div>
              <p className="text-sm whitespace-pre-wrap rounded-2xl border border-fynix-line p-3 bg-white">
                {campanha.mensagem_texto}
              </p>
            </div>

            {campanha.filtros && (
              <div className="text-xs text-fynix-muted">
                Filtros:{" "}
                {[
                  campanha.filtros.estado && `UF ${campanha.filtros.estado}`,
                  campanha.filtros.cidade && campanha.filtros.cidade,
                  campanha.filtros.plano_id && "plano selecionado",
                ]
                  .filter(Boolean)
                  .join(" · ") || "toda a base com telefone"}
              </div>
            )}

            <div>
              <h4 className="text-sm font-semibold mb-2">Envios</h4>
              {isLoading ? (
                <div className="flex justify-center py-6 text-fynix-muted">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              ) : envios.length === 0 ? (
                <p className="text-sm text-fynix-muted">Nenhum envio registrado ainda. Dispare a campanha para gerar a lista.</p>
              ) : (
                <div className="rounded-2xl border border-fynix-line overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-fynix-surface-muted">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Contato</th>
                        <th className="text-left px-3 py-2 font-medium">Telefone</th>
                        <th className="text-left px-3 py-2 font-medium">Status</th>
                        <th className="text-left px-3 py-2 font-medium">Quando</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-fynix-line">
                      {envios.map((e) => (
                        <tr key={e.id}>
                          <td className="px-3 py-2">{e.nome_contato || "—"}</td>
                          <td className="px-3 py-2">{e.telefone}</td>
                          <td className="px-3 py-2">
                            <span className={e.status_envio === "erro" ? "text-red-600" : "text-fynix-brand-dark"}>
                              {e.status_envio}
                            </span>
                            {e.erro_detalhe && <div className="text-red-500">{e.erro_detalhe}</div>}
                          </td>
                          <td className="px-3 py-2 text-fynix-muted">{formatDataHora(e.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
